// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { buttonTransparent } from "@hardwoods/XDL/PreStyle/button";
import { color } from "@hardwoods/XDL/Utilities/index";
import { ButtonPresentationProps, ButtonSizeVariants } from "@insite/mobius/Button";
import { IconWrapper } from "@insite/mobius/Icon";
import { LinkPresentationProps } from "@insite/mobius/Link";
import { TypographyProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { StyledProp } from "@insite/mobius/utilities/InjectableCss";
import { css } from "styled-components";

interface IconButtonColors {
    default: string;
    hover: string;
    hoverAlpha: number;
}

interface IconButtonProps {
    variantType?: "remove" | "secondary" | string;
    sizeVariant?: ButtonSizeVariants;
    colors?: IconButtonColors;
    typographyProps?: TypographyProps;
    css?: StyledProp<any>;
    [key: string]: any;
}

const getDefaultColor = (variantType?: string): IconButtonColors => {
    let color: IconButtonColors;
    switch (variantType) {
        case "secondary": {
            color = { default: "secondary", hover: "secondary.dark", hoverAlpha: 1 };
            break;
        }
        case "remove": {
            color = { default: "danger", hover: "danger.dark", hoverAlpha: 0.5 };
            break;
        }
        default: {
            // default = Primary
            color = { default: "primary", hover: "primary.hover", hoverAlpha: 1 };
            break;
        }
    }

    return color;
};

export const iconButtonCommonStyles = (iconSrc: React.ComponentType | string, buttonProps?: IconButtonProps) => {
    const sizeVariant = buttonProps?.sizeVariant ?? "small";
    const colors = { ...getDefaultColor(buttonProps?.variantType), ...buttonProps?.colors };

    return {
        variant: "tertiary",
        buttonType: "solid",
        sizeVariant,
        icon: {
            src: iconSrc,
            position: "left",
        },
        typographyProps: {
            weight: "normal",
            ...buttonProps?.typographyProps,
            css: css`
                letter-spacing: 0;
                line-height: 22px;
                ${buttonProps?.typographyProps?.css}
            `,
        },
        css: css`
            padding: 0 10px;
            color: ${color(colors?.default)};
            ${buttonTransparent}

            ${IconWrapper} {
                margin-right: 5px;
                opacity: 0.33;
                color: ${color(colors?.default)};
            }

            &:hover:not([disabled]),
            &:focus:not([disabled]),
            &:focus:hover {
                &,
                & ${IconWrapper} {
                    color: ${color(colors?.hover, colors?.hoverAlpha)};
                }
            }

            &:disabled {
                & ${TypographyStyle}, & ${IconWrapper} {
                    color: ${color(colors?.default, 0.5)};
                    background-color: transparent;
                }
            }

            ${buttonProps?.css}
        `,
    } as ButtonPresentationProps;
};

interface IconLinkProps {
    variantType?: "remove" | "secondary" | string;
    colors?: IconButtonColors;
    sizeVariant?: "medium" | "small";
    typographyProps?: TypographyProps;
    css?: StyledProp<any>;
    [key: string]: any;
}

export const iconLinkCommonStyles = (iconSrc: React.ComponentType | string, linkProps?: IconLinkProps) => {
    const colors = { ...getDefaultColor(linkProps?.variantType), ...linkProps?.colors };
    return {
        color: colors.default,
        icon: {
            iconProps: {
                src: iconSrc,
                size: linkProps?.sizeVariant === "small" ? 12 : 14,
                color: colors.default,
                css: css`
                    opacity: 0.33;
                `,
            },
            position: "left",
        },
        typographyProps: {
            size: linkProps?.sizeVariant === "small" ? 12 : 14,
            weight: "normal",
            ...linkProps?.typographyProps,
            css: css`
                letter-spacing: 0;
                line-height: 22px;
                ${linkProps?.typographyProps?.css}
            `,
        },
        hoverStyle: {
            color: color(colors.hover, colors.hoverAlpha)(),
        },
        css: css`
            &:disabled {
                &,
                & ${TypographyStyle}, & ${IconWrapper} {
                    color: ${color(colors.default, 0.5)};
                }
            }

            ${linkProps?.css}
        `,
    } as LinkPresentationProps;
};
